import { ColorTranslator } from 'colortranslator';

export const DEFAULT_COLORS = [
	'#E74355',
	'#FF8733',
	'#FFD646',
	'#2CC99A',
	'#06B6D4',
	'#2663EB',
	'#9A6BFF',
	'#FF6EB4',
] as const;

export function getStableColorFromString(value: string): string {
	if (!value) return DEFAULT_COLORS[0]!;

	let hash = 0;

	for (const char of value) {
		hash = ((hash << 5) - hash) + (char.codePointAt(0) ?? 0);
		hash = hash & hash;
	}

	const index = Math.abs(hash) % DEFAULT_COLORS.length;
	return DEFAULT_COLORS[index]!;
}

export interface ColorInfo {
	isDark: boolean;
	isLight: boolean;
}

export function isColorValid(color: string): boolean {
	try {
		ColorTranslator.toHEX(color);
		return true;
	}
	catch {
		return false;
	}
}

/**
 * Analyzes a color string and returns information about its luminance
 *
 * @param color - Any valid CSS color string (hex, rgb, hsl, etc.)
 * @param darkThreshold - CIEL value below which a color is considered dark (default: 20)
 * @param lightThreshold - CIEL value above which a color is considered light (default: 80)
 * @returns Object containing isDark and isLight boolean properties
 */
export function getColorInfo(
	color?: string | null,
	darkThreshold: number = 20,
	lightThreshold: number = 80,
): ColorInfo {
	if (!color) {
		return { isDark: false, isLight: false };
	}

	try {
		const luminance = new ColorTranslator(color).CIEL;

		return {
			isDark: luminance <= darkThreshold,
			isLight: luminance >= lightThreshold,
		};
	}
	catch {
		return { isDark: false, isLight: false };
	}
}

/**
 * @param val - Color component value (0-255)
 * @returns Linear RGB value (0-1) for accurate luminance calculations
 */
function normalizeColor(val: number) {
	const normalized = val / 255;
	return normalized <= 0.039_28
		? normalized / 12.92
		: ((normalized + 0.055) / 1.055) ** 2.4;
}

/**
 * Calculates relative luminance of a color according to WCAG guidelines
 * Used for determining contrast ratios and accessibility compliance
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 *
 * @param color - Any valid CSS color string
 * @returns Relative luminance value (0-1) where 0 is black and 1 is white
 */
function getRelativeLuminance(color: string): number {
	const colorTranslator = new ColorTranslator(color);
	const rgbObject = colorTranslator.RGBObject;

	const r = normalizeColor(rgbObject.R);
	const g = normalizeColor(rgbObject.G);
	const b = normalizeColor(rgbObject.B);

	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 *
 * @param foreground
 * @param background
 * @returns Contrast ratio (0-21)
 */
export function getContrastRatio(foreground: string, background: string): number {
	const foregroundLuminance = getRelativeLuminance(foreground);
	const backgroundLuminance = getRelativeLuminance(background);

	const lighterLuminance = Math.max(foregroundLuminance, backgroundLuminance);
	const darkerLuminance = Math.min(foregroundLuminance, backgroundLuminance);

	return (lighterLuminance + 0.05) / (darkerLuminance + 0.05);
}
